import type { CartLine, Coupon, PriceBreakdown, Tenant } from '@/types';

export const cartSubtotal = (lines: CartLine[]) =>
  lines.filter((l) => !l.savedForLater).reduce((s, l) => s + l.price * l.quantity, 0);

export const cartCount = (lines: CartLine[]) =>
  lines.filter((l) => !l.savedForLater).reduce((s, l) => s + l.quantity, 0);

export function couponDiscount(coupon: Coupon | undefined, subtotal: number) {
  if (!coupon || subtotal < coupon.minOrder) return 0;
  const raw = coupon.type === 'PERCENTAGE' ? (subtotal * coupon.value) / 100 : coupon.value;
  return Math.min(Math.round(coupon.maxDiscount ? Math.min(raw, coupon.maxDiscount) : raw), subtotal);
}

export interface PricingInput {
  lines: CartLine[];
  tenant: Pick<Tenant, 'shipping' | 'taxRate'>;
  coupon?: Coupon;
  walletToUse?: number;
  pointsToUse?: number;
  pointValue?: number;
}

/** Single source of truth for cart/checkout totals. The server must re-compute this authoritatively. */
export function computePricing(i: PricingInput): PriceBreakdown {
  const subtotal = cartSubtotal(i.lines);
  const discount = couponDiscount(i.coupon, subtotal);
  const after = subtotal - discount;
  const shipping = subtotal === 0 || subtotal >= i.tenant.shipping.freeThreshold ? 0 : i.tenant.shipping.flatFee;
  const total = after + shipping;
  const pointsApplied = Math.min(Math.round((i.pointsToUse ?? 0) * (i.pointValue ?? 1)), total);
  const walletApplied = Math.min(i.walletToUse ?? 0, total - pointsApplied);
  return {
    subtotal,
    couponDiscount: discount,
    walletApplied,
    pointsApplied,
    shipping,
    taxIncluded: Math.round((total * i.tenant.taxRate) / (100 + i.tenant.taxRate)),
    total,
    payable: total - walletApplied - pointsApplied,
  };
}
